import React from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom-v5-compat'

import Loader from '@/app/component/atom/loader'
import { ZOrgReportQuery } from '@/app/module/campaigns/types/schemas/reports/org-report'
import { useExportOrgReport } from '@/app/module/reports/store/hooks'
import { OrgReportFilter, OrgReportGrouping } from '@/app/module/reports/types'
import { useRequirePermission } from '@/app/module/user/store/ts/hooks'
import { selectUserLoading } from '@/app/module/user/store/ts/selectors'
import { captureError } from '@/app/service/util/error'

import NoReportPermission from './no-report-permission'
import SpendReport from './spend-report'
import SpendReportListContainer from './spend-report-list.container'

type Props = {
  isCreateMode?: boolean
}

const SpendReportContainer: React.FC<Props> = ({ isCreateMode = false }) => {
  const [exportOrgReport, { isLoading }] = useExportOrgReport()
  const location = useLocation()
  const navigate = useNavigate()
  const hasPermission = useRequirePermission('downloadReport')
  const userLoading = useSelector(selectUserLoading)

  const query = React.useMemo(() => {
    const params = new URLSearchParams(location.search)
    const q = params.get('query')

    if (!q) {
      return undefined
    }

    try {
      const jsonQuery = JSON.parse(q)
      return ZOrgReportQuery.parse(jsonQuery)
    } catch (error) {
      captureError(error)
      return undefined
    }
  }, [location.search])

  const handleSubmit = React.useCallback(
    async (grouping: OrgReportGrouping, filter?: OrgReportFilter) => {
      await exportOrgReport(grouping, filter)
      navigate('/reports/orgspend')
    },
    [exportOrgReport, navigate],
  )

  const handleWizardOpen = React.useCallback(() => {
    navigate('/reports/orgspend/create')
  }, [navigate])

  const handleCancel = React.useCallback(() => {
    navigate('/reports/orgspend')
  }, [navigate])

  return (
    <>
      {!userLoading && !hasPermission && <NoReportPermission />}
      {hasPermission && isCreateMode && (
        <SpendReport isLoading={isLoading} query={query} onSubmit={handleSubmit} onCancel={handleCancel} />
      )}
      {hasPermission && !isCreateMode && <SpendReportListContainer onCreateClick={handleWizardOpen} />}
      {isLoading && <Loader />}
    </>
  )
}

export default SpendReportContainer
